import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { MANAGED_LEAD_DETAIL_BASE_PATHS } from '../../../../config/managedLeadNavigation';
import useDateFormat from '../../../../hooks/useDateFormat';
import {
  approveManagedDropRequest,
  ManagedDropReviewRequest,
  rejectManagedDropRequest,
} from '../../../../services/api/managed-leads/managedDropReview';
import { ManagedLeadProductPath } from '../../../../services/api/managed-leads/managedLeads';
import {
  EMPTY_MANAGED_LEAD_VALUE,
  formatManagedLeadDate,
  formatManagedLeadValue,
} from '../../../managedLead/managedLeadFormatting';
import {
  managedDropReviewQueryKey,
  managedLeadDetailQueryKey,
  managedLeadListQueryKey,
} from '../../../managedLead/managedLeadQueryKeys';
import {
  describeManagedDropReviewFailure,
  ManagedDropReviewOutcome,
  MANAGED_DROP_REVIEW_APPROVED_MESSAGE,
  MANAGED_DROP_REVIEW_REJECTED_MESSAGE,
} from '../managedDropReviewFeedback';
import {
  formatManagedDropReviewStatus,
  managedDropReviewStatusColor,
} from '../managedDropReviewFormatting';
import DropRequestDatum from './DropRequestDatum';

/** Contract limit of the administrative motivation, not of a HubSpot Note. */
const MOTIVATION_MAX_LENGTH = 2000;

const MOTIVATION_REQUIRED_MESSAGE = 'La motivazione è obbligatoria.';
const MOTIVATION_TOO_LONG_MESSAGE = `La motivazione non può superare ${MOTIVATION_MAX_LENGTH} caratteri.`;

interface ManagedDropReviewCardProps {
  product: ManagedLeadProductPath;
  request: ManagedDropReviewRequest;
  /** Label of `stageAtRequest`, when the lifecycle that names it is loaded. */
  stageAtRequestLabel: string | null;
  /**
   * Whether this admin may decide. Only the review scope can, and the backend
   * enforces it anyway; hiding the buttons keeps the queue from offering an
   * action that would only come back as `insufficient_scope`.
   */
  canReview: boolean;
  /**
   * Where the account of a decision goes. It cannot stay here: an approval, a
   * rejection or a closed request leaves the filtered queue as soon as it is
   * re-read, and this card unmounts with the message still on it.
   */
  onOutcome: (outcome: ManagedDropReviewOutcome) => void;
}

/**
 * One request in the administrative queue: what was asked, on which lead, and
 * — while it is still pending — Approva and Rifiuta. Nothing here anticipates
 * the server: the card shows the state the queue last read, and a decision
 * changes it only once the server has confirmed it.
 */
const ManagedDropReviewCard = ({
  product,
  request,
  stageAtRequestLabel,
  canReview,
  onOutcome,
}: ManagedDropReviewCardProps) => {
  const queryClient = useQueryClient();
  const { formatDate } = useDateFormat();
  const [rejecting, setRejecting] = useState(false);
  const [motivation, setMotivation] = useState('');
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const inFlightRef = useRef(false);

  const invalidateAfterDecision = (movesTheLead: boolean) => {
    queryClient.invalidateQueries({
      queryKey: managedDropReviewQueryKey(product),
    });
    if (!movesTheLead) return;
    // An approval writes the Drop stage on HubSpot: the lead leaves In gestione
    // for the Drop list, and its detail no longer says what it said.
    queryClient.invalidateQueries({
      queryKey: managedLeadListQueryKey(product),
    });
    queryClient.invalidateQueries({
      queryKey: managedLeadDetailQueryKey(product, request.dealId),
    });
  };

  const reportFailure = (error: unknown, retry: () => void) => {
    const failed = describeManagedDropReviewFailure(error);
    if (failed.refetchesQueue) invalidateAfterDecision(false);
    onOutcome({
      requestId: request.id,
      message: failed.message,
      severity: failed.severity,
      retryable: failed.retryable,
      sessionExpired: failed.sessionExpired,
      retry,
    });
  };

  const reportSuccess = (message: string) =>
    onOutcome({
      requestId: request.id,
      message,
      severity: 'success',
      retryable: false,
      sessionExpired: false,
      retry: () => undefined,
    });

  const approval = useMutation({
    mutationFn: () => approveManagedDropRequest(product, request.id),
    onSuccess: () => {
      invalidateAfterDecision(true);
      reportSuccess(MANAGED_DROP_REVIEW_APPROVED_MESSAGE);
    },
    onError: (error) => reportFailure(error, approve),
    onSettled: () => {
      inFlightRef.current = false;
    },
  });

  const rejection = useMutation({
    mutationFn: (text: string) =>
      rejectManagedDropRequest(product, request.id, text),
    onSuccess: () => {
      // A rejection is local: the lead did not move, only the queue changed.
      invalidateAfterDecision(false);
      reportSuccess(MANAGED_DROP_REVIEW_REJECTED_MESSAGE);
      setRejecting(false);
      setMotivation('');
    },
    onError: (error) => reportFailure(error, submitRejection),
    onSettled: () => {
      inFlightRef.current = false;
    },
  });

  // Only the message under the Motivazione field is the card's business; the
  // account of the decision itself belongs to the queue.
  const rejectionFailure = rejection.isError
    ? describeManagedDropReviewFailure(rejection.error)
    : null;
  const deciding = approval.isPending || rejection.isPending;
  const deal = request.deal ?? null;
  const stageChanged = Boolean(
    deal && deal.stage.id && deal.stage.id !== request.stageAtRequest
  );

  const approve = () => {
    // A ref, not `isPending`: two clicks in the same tick both read the state
    // before React re-renders, and the second one must not reach the network.
    if (inFlightRef.current) return;
    setValidationMessage(null);
    rejection.reset();
    inFlightRef.current = true;
    approval.mutate();
  };

  const submitRejection = () => {
    if (inFlightRef.current) return;
    const trimmed = motivation.trim();
    if (!trimmed) {
      setValidationMessage(MOTIVATION_REQUIRED_MESSAGE);
      return;
    }
    if (trimmed.length > MOTIVATION_MAX_LENGTH) {
      setValidationMessage(MOTIVATION_TOO_LONG_MESSAGE);
      return;
    }
    setValidationMessage(null);
    approval.reset();
    inFlightRef.current = true;
    rejection.mutate(trimmed);
  };

  const motivationErrorMessage =
    validationMessage ?? rejectionFailure?.motivationFieldMessage ?? null;

  return (
    <Card component="article" variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box>
            <Typography component="h3" variant="h6">
              {deal
                ? formatManagedLeadValue(deal.name)
                : 'Lead non più disponibile'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`Deal ${request.dealId}`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Chip
              label={formatManagedDropReviewStatus(request.status)}
              color={managedDropReviewStatusColor(request.status)}
              size="small"
            />
          </Stack>
        </Stack>

        <Box
          component="dl"
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
          gap={2}
          m={0}
        >
          <DropRequestDatum
            label="Motivo"
            value={formatManagedLeadValue(request.reason?.label)}
          />
          <DropRequestDatum
            label="Data richiesta"
            value={formatManagedLeadDate(request.requestedAt, formatDate)}
          />
          <DropRequestDatum
            label="Fase alla richiesta"
            value={formatManagedLeadValue(
              stageAtRequestLabel ?? request.stageAtRequest
            )}
          />
          <DropRequestDatum
            label="Fase attuale"
            value={deal ? deal.stage.label : EMPTY_MANAGED_LEAD_VALUE}
          />
          <DropRequestDatum
            label="Data creazione lead"
            value={formatManagedLeadDate(deal?.createdAt, formatDate)}
          />
          <DropRequestDatum
            label="Proprietario lead"
            value={formatManagedLeadValue(deal?.ownerEmail)}
          />
          {request.status !== 'pending' && (
            <DropRequestDatum
              label="Data decisione"
              value={formatManagedLeadDate(request.decision.at, formatDate)}
            />
          )}
        </Box>

        <Stack spacing={2} mt={2}>
          {!deal && (
            <Alert severity="warning">
              La lead non è più leggibile su questo prodotto: non è possibile
              approvare la richiesta.
            </Alert>
          )}

          {stageChanged && (
            <Alert severity="info">
              {`La lead è passata a ${deal?.stage.label} dopo la richiesta.`}
            </Alert>
          )}

          {/* An approval that already began: HubSpot may hold the Drop while
              the local state does not. Retrying completes the local state and
              writes nothing more. */}
          {request.approvalInFlight && (
            <Alert severity="warning">
              Un&apos;approvazione di questa richiesta è già cominciata e non è
              stata completata. Riprova ad approvare per concluderla: non verrà
              applicata una seconda modifica su HubSpot.
            </Alert>
          )}

          {request.decision.motivation && (
            <Alert severity="info" icon={false}>
              <Typography variant="body2" fontWeight="bold">
                Motivazione del rifiuto
              </Typography>
              <Typography variant="body2">
                {request.decision.motivation}
              </Typography>
            </Alert>
          )}

          {request.actionable &&
            canReview &&
            (rejecting ? (
              <Stack
                component="form"
                spacing={2}
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  submitRejection();
                }}
              >
                <TextField
                  label="Motivazione del rifiuto"
                  required
                  multiline
                  minRows={3}
                  value={motivation}
                  onChange={(event) => {
                    setMotivation(event.target.value);
                    setValidationMessage(null);
                  }}
                  error={Boolean(motivationErrorMessage)}
                  helperText={
                    motivationErrorMessage ??
                    `${
                      motivation.trim().length
                    }/${MOTIVATION_MAX_LENGTH} caratteri. Resta nello storico e non diventa una nota HubSpot.`
                  }
                />
                <Stack direction="row" spacing={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={deciding}
                  >
                    {rejection.isPending
                      ? 'Invio in corso…'
                      : 'Conferma rifiuto'}
                  </Button>
                  <Button
                    color="inherit"
                    disabled={deciding}
                    onClick={() => {
                      setRejecting(false);
                      setValidationMessage(null);
                    }}
                  >
                    Annulla
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  disabled={deciding || !deal}
                  onClick={approve}
                >
                  {approval.isPending ? 'Invio in corso…' : 'Approva'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={deciding}
                  onClick={() => {
                    approval.reset();
                    setRejecting(true);
                  }}
                >
                  Rifiuta
                </Button>
              </Stack>
            ))}

          {deal && (
            <Box>
              <Button
                component={RouterLink}
                to={`${
                  MANAGED_LEAD_DETAIL_BASE_PATHS[product]
                }/${encodeURIComponent(request.dealId)}`}
                variant="text"
              >
                Visualizza i dettagli della lead
              </Button>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ManagedDropReviewCard;
